<script lang="ts" setup>
import VStack from '@/common/components/layouts/VStack.vue';
import HStack from '@/common/components/layouts/HStack.vue';
import DetailInformation from '@/common/components/molecules/DetailInformation.vue';
import AlertTargetList from '@/alert/components/alertTargetList/AlertTargetList.vue';
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import ModernGrid from '@/common/components/molecules/modernGrid/ModernGrid.vue';
import { type Props, setup } from '@/alert/components/alertDetail/alertInsightView.setup';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { capitalize, isNil } from 'lodash-es';

const props = defineProps<Props>();
const { t, alertData, alertInfo, targets, threshold, columns, rows, option } = setup(props);
</script>
<template>
  <section class="alert-insight-view">
    <v-stack :spacing="24">
      <v-stack :spacing="8">
        <span class="alert-insight-view__title">{{ t('WORD.INFORMATION') }}</span>
        <detail-information
          :use-header="false"
          :info-data-list="alertInfo"
        >
          <template #target>
            <alert-target-list
              :target-list="targets"
              is-clickable
              is-expand
            />
          </template>
          <template #threshold>
            <h-stack
              :spacing="12"
              class="alert-insight-view__threshold"
            >
              <h-stack
                v-if="threshold?.conditionType !== 'formula'"
                :spacing="12"
              >
                <span>{{ threshold?.evaluateValue }}</span>
                <span class="alert-insight-view__threshold-during">{{ t('WORD.DURING_THE') }}</span>
                <span>{{ threshold?.duringTimeInterval }}</span>
              </h-stack>
              <span class="alert-insight-view__threshold-comparator">
                {{ threshold?.comparator }}
              </span>
              <template
                v-for="(value, key) in threshold?.values"
                :key="key"
              >
                <v-stack
                  v-if="!isNil(value) && value !== ''"
                  :spacing="8"
                  align="center"
                >
                  <status-tag :type="key"> {{ capitalize(key) }} </status-tag>
                  <span>{{ value }}</span>
                </v-stack>
              </template>
              <span v-if="threshold?.conditionType !== 'formula'">{{ threshold?.unit }}</span>
            </h-stack>
          </template>
          <template #bottom-content>
            <h-stack
              class="alert-insight-view__info_bottom"
              :spacing="8"
              align="stretch"
            >
              <ev-icon icon="icon-featured-report" />
              <v-stack :spacing="8">
                <span class="alert-insight-view__title">{{ t('WORD.DETAILED_DESCRIPTION') }}</span>
                <div class="alert-insight-view__description">{{ alertData?.description }}</div>
              </v-stack>
            </h-stack>
          </template>
        </detail-information>
      </v-stack>
      <v-stack>
        <modern-grid
          :columns="columns"
          :rows="rows"
          :option="option"
          :env-key="USER_ENV_KEYS.GRIDCOLUMNS_alert_alertDetail_primaryCausesList"
        >
          <template #toolbarPrefix
            ><span class="alert-insight-view__title">{{ t('WORD.PRIMARY_CAUSES') }}</span>
          </template>
          <template #priority="{ item }">
            <status-tag
              :type="item.row.priority.toLowerCase()"
              :class="{ critical: item.row.priority === 'Critical' }"
            >
              {{ item.row.priority }}
            </status-tag>
          </template>
        </modern-grid>
      </v-stack>
      <v-stack
        v-if="alertData?.solutions && alertData?.solutions?.length > 0"
        :spacing="8"
      >
        <span class="alert-insight-view__title">{{ t('WORD.RESOLUTION') }}</span>
        <v-stack
          class="alert-insight-view__solution"
          :spacing="16"
        >
          <v-stack :spacing="16">
            <v-stack
              v-for="(solution, index) in alertData?.solutions"
              :key="index"
              :spacing="8"
              class="alert-insight-view__solution-item"
            >
              <span class="alert-insight-view__title">{{ solution.title }}</span>
              <h-stack :spacing="16">
                <h-stack :spacing="4">
                  <span>{{ t('WORD.PRIORITY') }}</span>
                  <status-tag
                    :type="solution.priority.toLowerCase()"
                    :class="{ critical: solution.priority === 'Critical' }"
                  >
                    {{ solution.priority }}
                  </status-tag>
                </h-stack>
                <h-stack :spacing="4">
                  <span>{{ t('WORD.DIFFICULTY') }}</span>
                  <span
                    :class="[
                      'alert-insight-view__text',
                      {
                        easy: solution.difficulty === 'Easy',
                        medium: solution.difficulty === 'Medium',
                        hard: solution.difficulty === 'Hard',
                      },
                    ]"
                  >
                    {{ t(`WORD.${solution.difficulty.toUpperCase()}`) }}
                  </span>
                </h-stack>
                <h-stack :spacing="4">
                  <span>{{ t('WORD.ESTIMATED_TIME') }}</span>
                  <span class="alert-insight-view__text alert-insight-view__estimated-time">
                    {{ solution.estimatedTime }}
                  </span>
                </h-stack>
              </h-stack>
              <h-stack
                :spacing="8"
                class="alert-insight-view__expected-effect"
              >
                <h-stack :spacing="4">
                  <ev-icon icon="icon-check-circle" />
                  <span class="alert-insight-view__text">{{ t('WORD.EXPECTED_EFFECT') }}</span>
                </h-stack>
                <span>{{ solution.expectedEffect }}</span>
              </h-stack>
            </v-stack>
          </v-stack>
        </v-stack>
      </v-stack>
    </v-stack>
  </section>
</template>
<style lang="scss" scoped>
:deep(.modern-grid-toolbar-container) {
  height: 24px;
  margin-bottom: 8px;
}
.alert-insight-view {
  .critical {
    border-color: var(--alert-insight-critical-border);
    background-color: var(--alert-insight-critical-bg);
    color: var(--alert-insight-critical-font);
  }

  &__title {
    color: var(--color-gray-00-12);

    @include typography('sub-title-1');
  }
  &__threshold {
    :deep(.status-tag) {
      min-width: 0;
    }
    &-during {
      color: var(--color-gray-02-10);
    }
    &-comparator {
      @include typography('sub-title-3');
    }
  }
  &__info_bottom {
    padding: 14px 16px;
    border: 1px solid var(--color-gray-05-04);
  }
  &__description {
    line-height: calc(1em + 4px) !important;
    white-space: pre-line;

    @include typography('body-2');
  }
  &__solution {
    padding: 16px;
    border-radius: 4px;
    background-color: var(--color-gray-07-01);
    color: var(--color-gray-02-10);

    @include typography('body-3');

    &-item {
      padding: 16px;
      border-radius: 4px;
      background-color: var(--color-gray-12-00);
      :deep(.status-tag) {
        min-width: 0;
      }
    }
  }
  &__text {
    line-height: 13px !important;
    color: var(--color-gray-00-11);
    &.easy {
      color: var(--color-green-05-05);
    }
    &.medium {
      color: var(--color-yellow-05-05);
    }
    &.hard {
      color: var(--color-red-05-05);
    }

    @include typography('sub-title-3');
  }
  &__estimated-time {
    color: var(--color-gray-02-10);
  }
  &__expected-effect {
    padding: 8px 12px 8px 8px;
    border-radius: 4px;
    background-color: var(--color-gray-08-01);
  }
}
</style>
